import { useEffect, useState } from "react";
import { AlertTriangle, Check, X, Clock, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const money = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

function useCountdown(expiresAt) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(expiresAt).getTime() - Date.now()));
  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setLeft(Math.max(0, new Date(expiresAt).getTime() - Date.now())), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const s = Math.floor(left / 1000);
  return { seconds: s, label: `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}` };
}

function DisputeCard({ dispute, rejectReasons, onRespond }) {
  const [mode, setMode] = useState(null); // null | "reject" | `alt:${id}`
  const [rejectReason, setRejectReason] = useState("");
  const [refundValue, setRefundValue] = useState("");
  const [minutes, setMinutes] = useState("");
  const [busy, setBusy] = useState(false);
  const { seconds, label } = useCountdown(dispute.expires_at);

  const alternatives = Array.isArray(dispute.alternatives) ? dispute.alternatives : [];
  const items = Array.isArray(dispute.metadata?.items) ? dispute.metadata.items : [];
  const evidences = Array.isArray(dispute.metadata?.evidences) ? dispute.metadata.evidences : [];
  const urgent = seconds > 0 && seconds < 120;

  const run = async (action, body) => {
    setBusy(true);
    try {
      await onRespond(dispute.dispute_id, action, body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-orange-800 dark:text-orange-300">
          #{String(dispute.pedido_codigo || "").replace("#", "") || dispute.order_id?.slice(0, 8)}
        </span>
        {dispute.ifood_display_id && (
          <span className="text-xs font-mono text-muted-foreground">iFood #{dispute.ifood_display_id}</span>
        )}
        <span className="text-sm font-medium truncate">{dispute.cliente_nome || "Cliente"}</span>
        <span className="text-xs text-muted-foreground">{money(dispute.pedido_total)}</span>
        <span
          className={`ml-auto inline-flex items-center gap-1 text-sm font-mono font-bold px-2 py-0.5 rounded ${
            urgent ? "bg-red-600 text-white animate-pulse" : "bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200"
          }`}
          title="Tempo para responder. Sem resposta, o iFood decide sozinho."
        >
          <Clock className="w-3.5 h-3.5" /> {seconds > 0 ? label : "expirado"}
        </span>
      </div>

      <p className="text-sm font-semibold">{dispute.action_label || dispute.action}</p>
      {dispute.message && <p className="text-sm text-muted-foreground italic">“{dispute.message}”</p>}
      {items.length > 0 && (
        <ul className="text-xs text-muted-foreground list-disc pl-5">
          {items.map((it, i) => (
            <li key={i}>
              {it.quantity ? `${it.quantity}x ` : ""}
              {it.name || it.externalCode || it.id}
              {it.reason ? ` — ${it.reason}` : ""}
            </li>
          ))}
        </ul>
      )}
      {evidences.length > 0 && (
        <div className="flex gap-2 flex-wrap text-xs">
          {evidences.map((ev, i) => (
            <a key={i} href={ev.url} target="_blank" rel="noreferrer" className="underline text-orange-800 dark:text-orange-300">
              ver evidência {i + 1}
            </a>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Sem resposta até o fim do prazo, o iFood aplica: <b>{dispute.timeout_action || "decisão automática"}</b>.
      </p>

      {mode === null && (
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" disabled={busy || seconds === 0} onClick={() => run("accept", {})}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Aceitar
          </Button>
          <Button size="sm" variant="destructive" disabled={busy || seconds === 0} onClick={() => setMode("reject")}>
            <X className="w-4 h-4 mr-1" /> Recusar
          </Button>
          {alternatives.map((alt) => (
            <Button key={alt.id} size="sm" variant="outline" disabled={busy || seconds === 0} onClick={() => setMode(`alt:${alt.id}`)}>
              {alt.type === "REFUND" ? "Propor reembolso parcial" : alt.type === "ADDITIONAL_TIME" ? "Pedir mais tempo" : `Propor ${alt.type}`}
            </Button>
          ))}
        </div>
      )}

      {mode === "reject" && (
        <div className="flex flex-col gap-2">
          <select
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          >
            <option value="">Motivo da recusa…</option>
            {Object.entries(rejectReasons || {}).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" disabled={!rejectReason || busy} onClick={() => run("reject", { reason: rejectReason })}>
              Confirmar recusa
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
              Voltar
            </Button>
          </div>
        </div>
      )}

      {mode?.startsWith("alt:") &&
        (() => {
          const alt = alternatives.find((a) => `alt:${a.id}` === mode);
          if (!alt) return null;
          const meta = alt.metadata || {};
          if (alt.type === "REFUND") {
            const max = Number(meta.maxAmount?.value || 0) / 100;
            return (
              <div className="flex flex-col gap-2">
                <label className="text-xs text-muted-foreground">Valor do reembolso (máximo {money(max)})</label>
                <div className="flex gap-2 items-center">
                  <Input type="number" step="0.01" min="0.01" max={max} value={refundValue} onChange={(e) => setRefundValue(e.target.value)} className="w-32 h-9" />
                  <Button size="sm" disabled={busy || !refundValue} onClick={() => run("alternative", { alternative_id: alt.id, type: "REFUND", amount: Number(refundValue) })}>
                    Propor
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
                    Voltar
                  </Button>
                </div>
              </div>
            );
          }
          const allowed = Array.isArray(meta.allowedsAdditionalTimeInMinutes) ? meta.allowedsAdditionalTimeInMinutes : [10, 15, 20, 30];
          return (
            <div className="flex flex-col gap-2">
              <label className="text-xs text-muted-foreground">Tempo extra</label>
              <div className="flex gap-2 items-center flex-wrap">
                <select className="h-9 rounded-md border bg-background px-2 text-sm" value={minutes} onChange={(e) => setMinutes(e.target.value)}>
                  <option value="">minutos…</option>
                  {allowed.map((m) => (
                    <option key={m} value={m}>
                      +{m} min
                    </option>
                  ))}
                </select>
                <Button size="sm" disabled={busy || !minutes} onClick={() => run("alternative", { alternative_id: alt.id, type: "ADDITIONAL_TIME", minutes: Number(minutes) })}>
                  Propor
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
                  Voltar
                </Button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

/**
 * Painel de negociações abertas do iFood — topo do Delivery. Cada negociação
 * tem prazo curto (~6 min); sem resposta o iFood decide sozinho e a loja pode
 * ter chargeback. Por isso o painel é alto-contraste e conta o tempo.
 */
export default function HandshakeBanner({ disputes, rejectReasons, onRespond }) {
  if (!Array.isArray(disputes) || disputes.length === 0) return null;
  return (
    <div className="p-2 sm:p-3 lg:p-4 pb-0">
      <div className="rounded-lg border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/30 overflow-hidden">
        <div className="bg-orange-500 text-white px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 animate-pulse" />
          <span className="font-bold text-sm sm:text-base">
            {disputes.length === 1 ? "iFood: O CLIENTE PEDIU UMA NEGOCIAÇÃO" : `iFood: ${disputes.length} NEGOCIAÇÕES AGUARDANDO RESPOSTA`}
          </span>
          <span className="ml-auto text-xs opacity-90">responda antes do prazo</span>
        </div>
        <div className="divide-y divide-orange-200 dark:divide-orange-900">
          {disputes.map((d) => (
            <DisputeCard key={d.dispute_id} dispute={d} rejectReasons={rejectReasons} onRespond={onRespond} />
          ))}
        </div>
      </div>
    </div>
  );
}
